import React from 'react';

import { usePublishTabEditSession } from 'Modules/UserAccount/Components/TabEditSession';
import { ToggleSwitch } from 'Views/Components/New/ToggleSwitch';
import { AccountDetailsTabHeader } from 'Views/Components/_HumanWritten/AccountDetails/AccountDetailsTabHeader';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import MessageBox from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { useCommunicationPreferences } from '../Hooks/useCommunicationPreferences';
import { usePreferenceEdits } from '../Hooks/usePreferenceEdits';
import { useUpdateCommunicationPreferences } from '../Hooks/useUpdateCommunicationPreferences';
import {
	CommunicationPreferenceChange,
	CommunicationPreferences,
} from '../Types/CommunicationPreferences';
import PreferenceTable from './PreferenceTable';
import { confirmPreferenceChange } from './ConfirmPreferenceChangeModal';

interface StaffCommunicationPreferencesProps {
	userId: string;
	/** For the confirmation, so the staff member sees whose account they are about to change. */
	customerName?: string;
	/**
	 * Called once a change has been saved. Every save writes a note on the customer, so the page can
	 * pull its notes again rather than showing a list that is missing what was just recorded.
	 */
	onSaved?: () => void;
}

/**
 * Staff view of a customer's communication preferences, with the authority to override what the
 * customer cannot — including the email rows that are otherwise locked on.
 *
 * <p>Saves behind a confirmation that requires a written reason, which the server writes to the
 * customer's notes and to the audit log in the same request. The overrides available here are
 * exactly the ones worth being deliberate about: switching off a cancellation email is not something
 * to do with a stray click.</p>
 */
export default function StaffCommunicationPreferences({
	userId,
	customerName,
	onSaved,
}: StaffCommunicationPreferencesProps) {
	const { data, isPending, isError } = useCommunicationPreferences(userId);
	const update = useUpdateCommunicationPreferences({ userId, silent: true });
	const edits = usePreferenceEdits(data);

	const save = () =>
		confirmPreferenceChange({
			customerName,
			summary: describeChanges(
				data!,
				edits.changes,
				edits.smsMasterChanged ? edits.smsMasterValue : undefined,
				edits.marketingChanged ? edits.marketingValue : undefined,
			),
			onConfirm: async reason => {
				await update.mutateAsync(edits.payload(reason));
				edits.discard();
				onSaved?.();
			},
		});

	// Save and Discard live in the page header, beside the account's own. Declared before the early
	// returns below because it is a hook; `data` is non-null wherever `hasChanges` can be true, since
	// there is nothing to change until it has loaded.
	usePublishTabEditSession(
		edits.hasChanges
			? { saving: update.isPending, onSave: save, onCancel: edits.discard, cancelLabel: 'Discard' }
			: null,
	);

	if (isPending) {
		return <LottieSpinner />;
	}

	if (isError || !data) {
		return (
			<MessageBox status="error" title="Could not load this customer's preferences">
				Refresh the page to try again.
			</MessageBox>
		);
	}

	const preferences: CommunicationPreferences = data;
	const offersSms = preferences.smsEnabled;

	return (
		<div className="flex flex-col gap-4">
			<AccountDetailsTabHeader headerText="Communications" />

			<MessageBox status="warning" title="You are changing a customer's preferences">
				Anything you change here overrides what the customer chose, including messages they
				would normally always receive. You will be asked to record why, and it is added to
				their notes.
			</MessageBox>

			{offersSms && !preferences.hasPhoneNumber && (
				<MessageBox status="info" slim>
					This customer has no mobile number, so no SMS will reach them whatever is set here.
				</MessageBox>
			)}

			<PreferenceTable
				rows={preferences.rows}
				valueOf={edits.valueOf}
				disabled={update.isPending}
				// Not muted for staff: the master switch is one of the things they may be changing, and
				// greying the rows out would hide what the customer gets back when it goes on again.
				smsMuted={false}
				showSms={preferences.smsEnabled}
				markRecommended
				onChange={edits.setCell}
			/>

			<div className="flex flex-col gap-4 border-t border-gray-200 pt-4">
				{offersSms && (
					<ToggleSwitch
						size="sm"
						label="Customer receives messages by SMS"
						description="The master switch. With this off, no SMS is sent whatever the rows above say."
						checked={edits.smsMasterValue}
						disabled={update.isPending}
						onChange={event => edits.setSmsMaster(event.target.checked)}
						data-test-id="staff-communication-preference-sms-master"
					/>
				)}

				{preferences.marketingEnabled && (
					<ToggleSwitch
						size="sm"
						label="Customer receives marketing communications"
						description="Marketing consent. Off also covers a customer who has never been asked."
						checked={edits.marketingValue}
						disabled={update.isPending}
						onChange={event => edits.setMarketing(event.target.checked)}
						data-test-id="staff-communication-preference-marketing"
					/>
				)}
			</div>

		</div>
	);
}

/**
 * The change list the staff member confirms, in their words rather than the payload's. Built from
 * the same `changes` that are about to be sent, so what they read is what is applied.
 */
function describeChanges(
	preferences: CommunicationPreferences,
	changes: CommunicationPreferenceChange[],
	smsMaster: boolean | undefined,
	marketing: boolean | undefined,
): string[] {
	const lines: string[] = [];

	if (smsMaster !== undefined) {
		lines.push(`SMS: ${smsMaster ? 'on' : 'off'}`);
	}

	changes.forEach(change => {
		const row = preferences.rows.find(candidate => candidate.key === change.category);
		const channel = change.channel === 'email' ? 'email' : 'SMS';
		lines.push(`${row?.name ?? change.category} by ${channel}: ${change.enabled ? 'on' : 'off'}`);
	});

	if (marketing !== undefined) {
		lines.push(`Marketing communications: ${marketing ? 'on' : 'off'}`);
	}

	return lines;
}
